import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./rendimentiPerAnnoSolare.module.scss";
import { formatItalianNumber } from "utils/formatUtils";

const RendimentiPerAnnoSolareChart = ({ data, isin, is_bond }) => {
  const { t } = useTranslation();

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  const renderCustomBarLabel = (props) => {
    const { x, y, width, value } = props;
    const isPositive = Number(value) >= 0;
    const yPos = isPositive ? y - 10 : y + 20;
    return (
      <text
        x={x + width / 2}
        y={yPos}
        fill={props.fill}
        textAnchor="middle"
        fontSize={12}
        fontFamily="Arial"
      >
        {`${formatItalianNumber(value, 1)}%`}
      </text>
    );
  };

  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      product_return: Math.round(item.product_return * 10) / 10,
      category_return: Math.round(Number(item.category_return) * 10) / 10,
    }));
  }, [data]);

  const generateYAxisTicks = (data) => {
    const allReturns = data.flatMap((d) => [
      Number(d.product_return),
      Number(d.category_return),
    ]);
    const step = 5;
    const buffer = 5;

    const min = Math.floor(Math.min(...allReturns) / step) * step - buffer;
    const max = Math.ceil(Math.max(...allReturns) / step) * step + buffer;

    const ticks = [];
    for (let i = min; i <= max; i += step) {
      ticks.push(i);
    }
    return ticks;
  };

  const yAxisTicks = useMemo(
    () => generateYAxisTicks(formattedData),
    [formattedData],
  );

  if (data.length === 0) {
    return null;
  }

  // Custom tooltip component with TypeScript interface
  interface TooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

  const CustomTooltip: React.FC<TooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          backgroundColor: colorsConfig.finecoWhite || "#fff",
          padding: "10px",
          border: `1px solid ${colorsConfig.FinecoGrayLight1 || "#ccc"}`,
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            marginBottom: "5px",
            fontWeight: "bold",
            fontFamily: "Arial",
            fontSize: "12px",
            color: colorsConfig.finecoBlack || "#000",
          }}
        >
          {`Anno: ${label}`}
        </p>
        {payload.map((entry, index) => (
          <p
            key={`tooltip-${index}`}
            style={{
              margin: "3px 0",
              fontFamily: "Arial",
              fontSize: "12px",
              color: entry.color,
            }}
          >
            {`${entry.name}: ${formatItalianNumber(entry.value, 1)}%`}
          </p>
        ))}
      </div>
    );
  };

  const CustomizedLegend = (props) => {
    const { payload } = props;
    return (
      <div className={styles.legendContainer}>
        <div>
          {payload.map((entry, index) => (
            <span key={`legend-item-${index}`} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{
                  backgroundColor: entry.color,
                }}
              ></span>
              <span className={styles.legendCopy}>{entry.value}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.sectionContainer}>
      <h2 className={styles.sectionTitle}>
        {t("productCard.rendimentiPerAnnoSolareTitle")}
      </h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={410} debounce={550}>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 10, left: -25, bottom: 0 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false} // ✨ no vertical lines
            />
            {/* Reference line at 0% */}
            <ReferenceLine
              y={0}
              stroke={colorsConfig.finecoBlack}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey="year"
              tick={chartCopy}
              tickLine={false}
              dy={10}
              strokeDasharray="none"
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tickFormatter={(value) => `${value}%`}
              ticks={yAxisTicks}
              domain={[Math.min(...yAxisTicks), Math.max(...yAxisTicks)]}
              tickLine={false}
              tick={chartCopy}
              axisLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            {/* <Legend
              align="left"
              verticalAlign="bottom"
              height={10}
              iconType="circle"
              iconSize={10}
              wrapperStyle={{
                paddingTop: "10px",
                paddingLeft: "18px",
              }}
              formatter={(value, entry) => (
                <span
                  style={{
                    color: colorsConfig.FinecoBlack,
                    fontSize: 12,
                    fontFamily: "Arial",
                  }}
                >
                  {value}
                </span>
              )}
            /> */}
            <Legend
              content={<CustomizedLegend />}
              align="left"
              verticalAlign="bottom"
              height={36} // Increased height to accommodate the footnote
            />
            <Bar
              dataKey="product_return"
              name={"Rendimenti " + isin}
              fill={colorsConfig.FinecoBluePrimary}
              barSize={26}
              radius={[4, 4, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
              isAnimationActive={false}
            />
            {!is_bond && (
              <Bar
                dataKey="category_return"
                name="Rendimento Categoria"
                fill={colorsConfig.CardCardGreen}
                barSize={26}
                radius={[4, 4, 0, 0]}
                label={(props) =>
                  renderCustomBarLabel({
                    ...props,
                    fill: colorsConfig.FinecoBlack,
                  })
                }
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RendimentiPerAnnoSolareChart;
